import Icons from './Icons.tsx';
import {Box, Typography, Chip} from '@mui/material';

const StatItem = ({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {icon}
      <Typography variant="body2">{label}</Typography>
    </Box>
    <Typography variant="body2" fontWeight={600}>{value}</Typography>
  </Box>
);

const CompactStatRow = ({
  min,
  avg,
  max,
  unit = '',
  decimals = 2
}: {
  min?: number | null;
  avg?: number | null;
  max?: number | null;
  unit?: string;
  decimals?: number;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {Icons.drawer.inline.minimum}
      <Typography variant="body2" fontWeight={600}>{`${min?.toFixed?.(decimals)} ${unit}` ?? '-'}</Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {Icons.drawer.inline.average}
      <Typography variant="body2" fontWeight={600}>{`${avg?.toFixed?.(decimals)} ${unit}` ?? '-'}</Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {Icons.drawer.inline.maximum}
      <Typography variant="body2" fontWeight={600}>{`${max?.toFixed?.(decimals)} ${unit}` ?? '-'}</Typography>
    </Box>
  </Box>
);

interface PathStatisticsProps {
  data: Position[];
}

const PathStatistics: React.FC<PathStatisticsProps> = ({
  data
}) => {
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {Icons.drawer.content.totals}
        <Typography variant="h6" fontWeight={600}>
          Totals
        </Typography>
      </Box>
      <StatItem icon={Icons.drawer.inline.points} label="Points" value={data.count} />
      <StatItem icon={Icons.drawer.inline.distance} label="Distance" value={`${data.totalDistance.toFixed(2)} km`} />
      <StatItem icon={Icons.drawer.inline.duration} label="Duration" value={`${data.totalDuration.toFixed(2)} h`} />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 2 }}>
        {Icons.drawer.content.altitude}
        <Typography variant="h6" fontWeight={600}>
          Altitude
        </Typography>
      </Box>
      <CompactStatRow
        min={data.minAltitude}
        avg={data.avgAltitude}
        max={data.maxAltitude}
        unit="m"
      />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 2 }}>
        {Icons.drawer.content.speed}
        <Typography variant="h6" fontWeight={600}>
          Speed
        </Typography>
      </Box>
      <CompactStatRow
        min={data.minSpeed}
        avg={data.avgSpeed}
        max={data.maxSpeed}
        unit="m/s"
      />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 2 }}>
        {Icons.drawer.content.accuracy}
        <Typography variant="h6" fontWeight={600}>
          Accuracy
        </Typography>
      </Box>
      <CompactStatRow
        min={data.minAccuracy}
        avg={data.avgAccuracy}
        max={data.maxAccuracy}
        unit="m"
      />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 2 }}>
        {Icons.drawer.content.bearing}
        <Typography variant="h6" fontWeight={600}>
          Bearing
        </Typography>
      </Box>
      <CompactStatRow
        min={data.minBearing}
        avg={data.avgBearing}
        max={data.maxBearing}
        unit="°"
      />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 2 }}>
        {Icons.drawer.content.provider}
        <Typography variant="h6" fontWeight={600}>
          Provider
        </Typography>
      </Box>
      {Object.entries(data.perProvider).map(([provider, count]) => (
        <Chip key={provider} label={`${provider}: ${count}`} sx={{ m: 0.5 }} />
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 2 }}>
        {Icons.drawer.content.source}
        <Typography variant="h6" fontWeight={600}>
          Source
        </Typography>
      </Box>
      {Object.entries(data.perSource).map(([source, count]) => (
        <Chip key={source} label={`${source}: ${count}`} sx={{ m: 0.5 }} />
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 2 }}>
        {Icons.drawer.content.travel_mode}
        <Typography variant="h6" fontWeight={600}>
          Travel Mode
        </Typography>
      </Box>
      {Object.entries(data.perTravelMode).map(([travelMode, count]) => (
        <Chip key={travelMode} label={`${travelMode}: ${count}`} sx={{ m: 0.5 }} />
      ))}
    </>
  );
};

export default PathStatistics;
